import React from 'react';
import {Box, Paper, Stack, Typography} from '@mui/material';
import {Trans} from '@lingui/react/macro';
import * as CuttingApi from '../../../apis/Cutting.api.ts';
import {CuttingStateDisplay} from '../../../types/models/CuttingState.d';
import {ID} from '../../../../types/global.d';
import ProviderSummaryCards from './ProviderSummaryCards';
import ProviderAttentionList from './ProviderAttentionList';
import StorageShipmentPies from '../../generic/report/StorageShipmentPies.tsx';
import TimelineRootReport from '../../generic/report/TimelineRootReport.tsx';
import GradientCircularProgress from '../../../../share/components/progress/GradientCircularProgress.tsx';

type ProviderOverviewTabProps = {
    providerId?: ID;
};

/**
 * The provider's own dashboard: what it cut, what it shipped and what is still
 * in the storage.
 *
 * Everything is scoped to the provider, so the numbers follow the provider the
 * page was opened for and not the whole installation.
 */
const ProviderOverviewTab = ({providerId}: ProviderOverviewTabProps) => {

    const providerIdRefs = React.useMemo(() => (providerId ? [providerId] : []), [providerId]);
    const hasProvider = providerIdRefs.length > 0;

    // Only the counts are needed from the cuttings, so one row is enough - the
    // total is what the count is for.
    const {data: cuttingsData, isFetching: isCuttingsFetching} = CuttingApi.useGetCuttingAdvancedPresentationQuery(
        {
            list: {page: 0, pageSize: 1},
            filter: {
                providerIdRefs,
                state: [],
                orderBys: [],
                caseSensitive: false,
                forestOwnerIdInclude: false,
            },
        } as any,
        {skip: !hasProvider}
    );

    const {data: activeCuttingsData} = CuttingApi.useGetCuttingAdvancedPresentationQuery(
        {
            list: {page: 0, pageSize: 1},
            filter: {
                providerIdRefs,
                state: [CuttingStateDisplay.InProgress] as any,
                orderBys: [],
                caseSensitive: false,
                forestOwnerIdInclude: false,
            },
        } as any,
        {skip: !hasProvider}
    );

    const totalCuttings = cuttingsData?.totalItems ?? 0;
    const activeCuttings = activeCuttingsData?.totalItems ?? 0;

    // The report components run their queries in an effect keyed on the filter
    // object, so it has to keep its identity between renders.
    const reportFilter = React.useMemo(() => ({providerId: providerIdRefs}), [providerIdRefs]);

    if (isCuttingsFetching && !cuttingsData) {
        return (
            <Stack sx={{alignItems: 'center', padding: '2em'}} data-testid="Provider-overview-loading">
                <GradientCircularProgress/>
            </Stack>
        );
    }

    if (hasProvider && totalCuttings === 0) {
        return (
            <Paper sx={{padding: '2em'}} data-testid="Provider-overview-empty">
                <Typography variant="body2">
                    <Trans>This provider has no cuttings yet.</Trans>
                </Typography>
            </Paper>
        );
    }

    return (
        <Stack spacing={3} data-testid="Provider-overview">
            <ProviderAttentionList providerId={providerId}/>

            <ProviderSummaryCards
                providerId={providerId!}
                totalCuttings={totalCuttings}
                activeCuttings={activeCuttings}
            />

            <Box>
                <Typography variant="h6" sx={{marginBottom: '1em'}}>
                    <Trans>Shipped and stored volume</Trans>
                </Typography>
                <StorageShipmentPies filter={reportFilter} showOnlyShipped={false}/>
            </Box>

            <Box>
                <TimelineRootReport
                    filter={reportFilter}
                    showCutting={false}
                    showStorage={false}
                    showForm={false}
                    initialState={{}}
                    initialPeriod="Month"
                />
            </Box>
        </Stack>
    );
};

export default ProviderOverviewTab;
